import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Mail, Search, Library, WifiOff, PenLine, ShieldCheck, Keyboard,
  Download, ListChecks, Pin, PlaneTakeoff, Monitor,
} from 'lucide-react';
import { BlurFade } from '../components/magicui/blur-fade';
import { Marquee } from '../components/magicui/marquee';
import { ShimmerButton } from '../components/magicui/shimmer-button';
import { AnimatedGridPattern } from '../components/magicui/animated-grid-pattern';
import ComingSoonBadge from '../components/ComingSoonBadge';
import ManualCard from '../components/ManualCard';
import { company } from '../data/site';

// Product details mirror the InstruKit (instrukit.com) product site. InstruKit is not released yet,
// so this page deliberately has no outbound link or download.
const capabilities = [
  { icon: Search, title: 'Universal manual finder', text: 'Search manufacturers, model lines and revisions from one command bar — typos and partial part numbers welcome.' },
  { icon: Library, title: 'A library that lives with you', text: 'Organise manuals into nested kits with custom tags, encrypted at rest on your own device.' },
  { icon: WifiOff, title: 'Offline-first reading room', text: 'Once fetched, a manual stays available without a connection, in a reader tuned for dense diagrams.' },
  { icon: PenLine, title: 'Annotations for the field', text: 'Anchored notes, highlighted torque tables and pinned diagrams that survive document revisions.' },
  { icon: ShieldCheck, title: 'Quiet by design', text: 'No background tracking, no profiling, no advertising — and no pop-ups during normal use.' },
  { icon: Keyboard, title: 'Built for technicians', text: 'Keyboard-first everything, plus one-step export to USB for the field laptop.' },
];

const steps = [
  { icon: Download, title: 'Install the kit', text: 'A signed, lightweight installer that creates no persistent background services.' },
  { icon: ListChecks, title: 'Index your devices', text: 'Type a model number or paste a parts list and InstruKit assembles a working set for your inventory.' },
  { icon: Pin, title: 'Annotate & organise', text: 'Pin the right diagrams and build kits per workshop, instead of juggling browser tabs.' },
  { icon: PlaneTakeoff, title: 'Take it offline', text: 'Cache everything before a job and sync changes when you return.' },
];

const tags = ['Service manuals', 'Schematics', 'Wiring diagrams', 'Parts lists', 'Torque tables', 'Offline library', 'Annotations', 'Custom kits', 'USB export', 'Windows 10 / 11'];

export default function InstruKit() {
  return (
    <>
      <section className="relative isolate overflow-hidden bg-white pb-20 pt-28 lg:pb-28 lg:pt-36">
        <AnimatedGridPattern
          numSquares={28}
          maxOpacity={0.1}
          duration={3}
          className="-z-10 fill-fusion-ember/40 stroke-ink/[0.06] [mask-image:radial-gradient(700px_circle_at_70%_20%,white,transparent)]"
        />
        <div className="container-x">
          <Link to="/#products" className="inline-flex items-center gap-2 text-sm text-ink-3 transition hover:text-ink">
            <ArrowLeft className="size-4" aria-hidden="true" /> All products
          </Link>
          <div className="mt-10 grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <BlurFade>
                <div className="flex flex-wrap items-center gap-2">
                  <ComingSoonBadge />
                  <p className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-1.5 text-xs font-medium text-ink-3 shadow-card">
                    A Market Fusion Innovations product
                  </p>
                </div>
              </BlurFade>
              <BlurFade delay={0.1}>
                <h1 className="mt-6 font-display text-[clamp(2.7rem,6vw,4.6rem)] font-semibold leading-[1.02] tracking-[-0.045em]">
                  InstruKit: the field manual that fits <span className="fusion-text">in your toolbox.</span>
                </h1>
              </BlurFade>
              <BlurFade delay={0.2}>
                <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-3">
                  InstruKit is a desktop kit for the people who fix things — a catalogue of manuals, schematics
                  and service notes that opens fast, indexes locally, and stays available when the workshop loses
                  signal. It is in final development and launching soon for Windows.
                </p>
              </BlurFade>
              <BlurFade delay={0.3}>
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <ShimmerButton
                    as="a"
                    href={`mailto:${company.email}?subject=${encodeURIComponent('InstruKit launch updates')}`}
                    background="#0A0F1F"
                    shimmerColor="#FFB08F"
                    className="gap-2 text-sm font-semibold"
                  >
                    <Mail className="size-4" aria-hidden="true" /> Ask about the launch
                  </ShimmerButton>
                  <span className="inline-flex items-center gap-2 text-sm text-ink-3">
                    <Monitor className="size-4" aria-hidden="true" /> Windows 10 / 11 · Free
                  </span>
                </div>
              </BlurFade>
            </div>
            <BlurFade delay={0.25} direction="up" offset={20} className="mx-auto w-full max-w-md lg:max-w-none">
              <ManualCard />
            </BlurFade>
          </div>
        </div>
      </section>

      <div className="border-y border-line bg-mist py-3">
        <Marquee pauseOnHover className="[--duration:30s] [--gap:2rem]">
          {tags.map((t) => (
            <span key={t} className="whitespace-nowrap rounded-full bg-white px-4 py-1.5 font-mono text-xs text-ink-2 shadow-card">
              {t}
            </span>
          ))}
        </Marquee>
      </div>

      <section className="bg-mist py-24 lg:py-28">
        <div className="container-x">
          <div className="max-w-2xl">
            <BlurFade inView><p className="eyebrow">What’s inside</p></BlurFade>
            <BlurFade inView delay={0.1}>
              <h2 className="h-section mt-5">Every manual you need, at the bench and off the grid.</h2>
            </BlurFade>
          </div>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {capabilities.map(({ icon: Icon, title, text }, i) => (
              <BlurFade key={title} inView delay={0.05 * i} className="h-full">
                <div className="group h-full rounded-3xl bg-white p-7 shadow-card transition-shadow hover:shadow-lift">
                  <span className="inline-flex size-11 items-center justify-center rounded-2xl bg-mist text-[#C2410C] transition-colors group-hover:bg-ink group-hover:text-white">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold">{title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-3">{text}</p>
                </div>
              </BlurFade>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-28">
        <div className="container-x">
          <div className="mx-auto max-w-2xl text-center">
            <BlurFade inView><p className="eyebrow justify-center">How it will work</p></BlurFade>
            <BlurFade inView delay={0.1}>
              <h2 className="h-section mt-5">From install to job site in four steps.</h2>
            </BlurFade>
          </div>
          <ol className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {steps.map(({ icon: Icon, title, text }, i) => (
              <li key={title}>
                <BlurFade inView delay={0.08 * i} className="relative h-full rounded-3xl border border-line p-7">
                  <span className="font-mono text-xs text-ink-3">Step {String(i + 1).padStart(2, '0')}</span>
                  <span className="mt-5 flex size-12 items-center justify-center rounded-2xl bg-gradient-to-br from-fusion-ember to-fusion-violet text-white">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{text}</p>
                </BlurFade>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-white pb-24 lg:pb-32">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-[2.25rem] bg-ink px-7 py-14 text-white sm:px-12 lg:px-16 lg:py-20">
            <div aria-hidden="true" className="absolute -right-24 -top-24 size-96 rounded-full bg-fusion-ember/25 blur-3xl" />
            <div aria-hidden="true" className="absolute -bottom-32 left-10 size-80 rounded-full bg-fusion-violet/25 blur-3xl" />
            <div className="relative max-w-2xl">
              <BlurFade inView><ComingSoonBadge dark /></BlurFade>
              <BlurFade inView delay={0.1}>
                <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-[-0.035em] text-balance sm:text-5xl">
                  InstruKit is almost ready for your bench.
                </h2>
              </BlurFade>
              <BlurFade inView delay={0.2}>
                <p className="mt-5 max-w-lg text-white/70">
                  We are putting the finishing touches on InstruKit. Free, signed and offline-first — launching soon
                  for Windows 10 and 11. Questions or partnership enquiries are welcome in the meantime.
                </p>
              </BlurFade>
              <BlurFade inView delay={0.3}>
                <ShimmerButton
                  as="a"
                  href={`mailto:${company.email}?subject=${encodeURIComponent('InstruKit launch updates')}`}
                  background="#FF7A45"
                  className="mt-8 inline-flex w-fit gap-2 text-sm font-semibold"
                >
                  <Mail className="size-4" aria-hidden="true" /> Contact the team
                </ShimmerButton>
              </BlurFade>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
